import React from "react";
import ComparisonPageTemplate from "../../components/ComparisonPageTemplate/ComparisonPageTemplate";

const CANONICAL_URL = "https://teamgrid.ai/compare/teamgrid-ai-vs-hubstaff";

const FAQ_ITEMS = [
  {
    question: "How is TeamGrid different from Hubstaff?",
    answer: "TeamGrid is designed to help leaders understand why work happens the way it does by using AI to surface patterns, trends, and potential risks. Hubstaff primarily focuses on tracking time and activity. TeamGrid emphasizes insight and context rather than behavior-level monitoring.",
  },
  {
    question: "What is Ask TeamGrid?",
    answer: "Ask TeamGrid is an AI-powered chat interface that allows leaders to ask practical questions such as where work may be slowing down, which teams appear overloaded, or what areas need attention. It provides clear summaries and downloadable reports without requiring manual data analysis.",
  },
  {
    question: "Is TeamGrid suitable for enterprise teams?",
    answer: "Yes. TeamGrid is built to scale across teams and departments. It is well suited for organizations that need visibility, informed decision-making, and performance insights without relying on micromanagement or surveillance-based practices.",
  },
  {
    question: "Does TeamGrid work for remote and hybrid teams?",
    answer: "Yes. TeamGrid is designed to work equally well for remote, hybrid, and in-office teams by providing consistent visibility across different working models.",
  },
  {
    question: "How do I book a TeamGrid demo?",
    answer: "You can easily book a live demo session through our website. Just follow these steps: Go to Book a Demo → Select a date → Choose a time slot → Fill in your details → Confirm the booking. A member of our team will reach out to guide you through the platform.",
  },
];

const JSON_LD_FAQ = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${CANONICAL_URL}#faq`,
  mainEntity: [
    {
      "@type": "Question",
      name: "How is TeamGrid different from Hubstaff?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "TeamGrid helps leaders understand why work happens the way it does by using AI to surface patterns, trends, and potential risks. Hubstaff primarily focuses on tracking time and activity. TeamGrid emphasizes insight and context rather than behavior-level monitoring.",
      },
    },
    {
      "@type": "Question",
      name: "What is Ask TeamGrid?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Ask TeamGrid is an AI-powered chat interface that allows leaders to ask practical questions such as where work may be slowing down, which teams appear overloaded, or what areas need attention. It provides clear summaries and downloadable reports without requiring manual data analysis.",
      },
    },
    {
      "@type": "Question",
      name: "Is TeamGrid suitable for enterprise teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid is built to scale across teams and departments. It is well suited for organizations that need visibility, informed decision-making, and performance insights without relying on micromanagement or surveillance-based practices.",
      },
    },
    {
      "@type": "Question",
      name: "Does TeamGrid work for remote and hybrid teams?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. TeamGrid works for remote, hybrid, and in-office teams by providing consistent visibility across different working models with privacy-first principles.",
      },
    },
    {
      "@type": "Question",
      name: "How do I book a TeamGrid demo?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You can book a live demo session through the website by going to Book a Demo, selecting a date, choosing a time slot, filling in your details, and confirming the booking. A team member will reach out to guide you through the platform.",
      },
    },
  ],
};

const JSON_LD_GRAPH = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebSite",
      "@id": "https://teamgrid.ai/#website",
      url: "https://teamgrid.ai/",
      name: "TeamGrid",
    },
    {
      "@type": "Organization",
      "@id": "https://teamgrid.ai/#organization",
      name: "TeamGrid",
      url: "https://teamgrid.ai/",
    },
    {
      "@type": "WebPage",
      "@id": `${CANONICAL_URL}#webpage`,
      url: CANONICAL_URL,
      name: "The Best Hubstaff Alternative for Insight-Driven Teams",
      description: "Hubstaff shows you what happened. TeamGrid helps you understand why it happened using AI to surface patterns, risks, and improvement areas—making it a better Hubstaff alternative for trust and clarity.",
      isPartOf: { "@id": "https://teamgrid.ai/#website" },
      about: [
        { "@id": "https://teamgrid.ai/#teamgrid-app" },
        { "@id": "https://hubstaff.com/#hubstaff-app" },
      ],
      breadcrumb: { "@id": `${CANONICAL_URL}#breadcrumbs` },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${CANONICAL_URL}#breadcrumbs`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://teamgrid.ai/" },
        { "@type": "ListItem", position: 2, name: "Compare", item: "https://teamgrid.ai/compare/" },
        { "@type": "ListItem", position: 3, name: "TeamGrid vs Hubstaff", item: CANONICAL_URL },
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://teamgrid.ai/#teamgrid-app",
      name: "TeamGrid",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS",
      description: "TeamGrid is an AI-powered workforce intelligence platform that helps teams understand work patterns and make better decisions without surveillance.",
      url: "https://teamgrid.ai/",
      publisher: { "@id": "https://teamgrid.ai/#organization" },
      featureList: [
        "Workforce intelligence and decision support",
        "AI-powered insights to explain trends and patterns automatically",
        "Ask TeamGrid (conversational AI for instant answers)",
        "Privacy-first design with transparency and trust",
        "Team-level trends and organizational insights",
        "Daily, hourly, and weekly summaries",
        "One-click, shareable executive-ready reports",
        "Quick onboarding and easy adoption across team structures",
      ],
    },
    {
      "@type": "SoftwareApplication",
      "@id": "https://hubstaff.com/#hubstaff-app",
      name: "Hubstaff",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Windows, macOS, Web",
      description: "Hubstaff is a time tracking platform focused on tracking time and activity with activity-led dashboards and standard exports.",
      url: "https://hubstaff.com/",
      publisher: { "@type": "Organization", name: "Hubstaff", url: "https://hubstaff.com/" },
      featureList: [
        "Time and activity tracking",
        "Time and activity dashboards",
        "Report-based summaries",
        "Standard exports such as CSV",
      ],
    },
    {
      "@type": "ItemList",
      "@id": `${CANONICAL_URL}#comparison`,
      name: "TeamGrid vs Hubstaff - Feature Comparison",
      description: "Comparison of TeamGrid and Hubstaff across philosophy, AI insights, conversational AI, privacy approach, visibility model, dashboards, organizational insights, summaries, and exports.",
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      numberOfItems: 2,
      itemListElement: [
        { "@type": "ListItem", position: 1, item: { "@id": "https://teamgrid.ai/#teamgrid-app" } },
        { "@type": "ListItem", position: 2, item: { "@id": "https://hubstaff.com/#hubstaff-app" } },
      ],
    },
  ],
};

const COMPARISON_TABLE_ROWS = [
  { feature: "Core Philosophy", teamgrid: "Understanding work patterns", competitor: "Tracking time & activity" },
  { feature: "AI-Powered Insights", teamgrid: "Explains trends and patterns automatically", competitor: "Data typically reviewed through reports" },
  { feature: "Ask TeamGrid (AI Chat)", teamgrid: "Ask questions and get instant answers", competitor: "Not available" },
  { feature: "Privacy-Oriented Design", teamgrid: "Designed with transparency and trust in mind", competitor: "Includes monitoring-oriented features" },
  { feature: "Approach to Visibility", teamgrid: "Insight-led by default", competitor: "Activity-led by default" },
  { feature: "Dashboards", teamgrid: "Summaries focused on clarity and insight", competitor: "Time and activity dashboards" },
  { feature: "Organizational Insights", teamgrid: "Highlights potential issues and improvement areas", competitor: "Requires manual review of reports" },
  { feature: "Work Summaries", teamgrid: "Daily, hourly, and weekly summaries", competitor: "Report-based summaries" },
  { feature: "Export & Sharing", teamgrid: "One-click, shareable reports", competitor: "Standard exports such as CSV" },
];

const DATA_DECISIONS_TEAMGRID_BULLETS = [
  "Visibility comes from aggregated work patterns rather than individual-level observation",
  "Leaders understand how teams operate without focusing on behavior monitoring",
  "Teams tend to adopt the platform more naturally, with minimal change management",
  "This approach is why many organizations view TeamGrid as a better than Hubstaff option for insight-led environments",
];

const DATA_DECISIONS_HUBSTAFF_BULLETS = [
  "Visibility is driven by time logs, activity levels, and monitoring signals",
  "Data is available, but teams may feel closely observed",
  "Compliance may improve, while engagement and trust can vary by team",
];

const DATA_DECISIONS2_TEAMGRID_BULLETS = [
  "AI highlights patterns, friction points, and areas that may need attention",
  "Daily and weekly summaries make trends easier to understand",
  "Focuses on guiding actions and decisions, not just reporting numbers",
  "This approach is why many teams consider TeamGrid a better than Hubstaff option when clarity and speed matter",
];

const DATA_DECISIONS2_HUBSTAFF_BULLETS = [
  "Provides strong time and activity tracking capabilities",
  "Managers often spend time reviewing and interpreting raw data",
  "Insights depend heavily on individual experience and analytical effort",
  "Decision-making can vary based on how data is understood",
];

const WHAT_YOU_GET_ITEMS = [
  "Ask TeamGrid (AI chat for reports and insights)",
  "Organizational Intelligence Engine",
  "Real-time dashboards",
  "Daily, hourly, and weekly summaries",
  "One-click report downloads",
  "Works across all team setups",
  "Quick onboarding and easy adoption",
  "Surveillance-free by default",
];

const KEY_FEATURE_CARDS = [
  {
    title: "Ask TeamGrid: Your AI Workforce Assistant",
    intro: "Leaders can ask simple, practical questions such as:",
    bullets: [
      "Where is the team losing focus?",
      "Which functions appear overloaded right now?",
      "What may be slowing delivery or affecting quality?",
    ],
    closing: "TeamGrid provides clear, downloadable insights that reduce the need for spreadsheets or manual data analysis.",
  },
  {
    title: "Organizational Intelligence (Not Just Tracking)",
    intro: "TeamGrid helps surface:",
    bullets: [
      "Workflow friction,",
      "Team bottlenecks,",
      "Early burnout signals,",
      "Areas for improvement",
      "All identified automatically, without constant monitoring.",
    ],
    closing: "",
  },
];

export default function HubstaffComparison() {
  return (
    <ComparisonPageTemplate
      seoTitle="Hubstaff Alternative | AI-Powered Insights with TeamGrid"
      seoDescription="Looking for a Hubstaff alternative? TeamGrid delivers AI-driven workforce insights without surveillance built for trust, clarity, and better decisions."
      seoCanonical={CANONICAL_URL}
      jsonLdGraph={JSON_LD_GRAPH}
      jsonLdFaq={JSON_LD_FAQ}
      heroBadge="Comparison Guide"
      heroTitleLine1="The Best Hubstaff Alternative for Insight-Driven Teams"
      heroTitleLine2="Where Hubstaff Ends with Tracking, TeamGrid Begins with Insight"
      heroTitleLine2Teal
      heroTitleLine3={null}
      heroTitleLine4={null}
      heroDescription="Hubstaff shows you what happened. TeamGrid helps you understand why it happened using AI to surface patterns, risks, and improvement areas. That's why growing teams choose TeamGrid as a better than Hubstaff alternative."
      competitorName="Hubstaff"
      competitorLogoUrl="/hubstaff_logo.webp"
      comparisonTableRows={COMPARISON_TABLE_ROWS}
      keyFeaturesTitle="Why Teams Choose TeamGrid Over Hubstaff"
      keyFeaturesSubtitle="Ask TeamGrid: Your AI Workforce Assistant"
      keyFeatureCards={KEY_FEATURE_CARDS}
      dataDecisionsHeading="Use Case 1: Understanding Work Without Watching Every Move"
      dataDecisionsSubtitle="As teams scale, leaders need clear visibility into how work is progressing without creating friction, resistance, or cultural strain. Traditional monitoring tools can provide data, but long-term adoption often depends on how that visibility is delivered and perceived by teams."
      dataDecisionsTeamGridBullets={DATA_DECISIONS_TEAMGRID_BULLETS}
      dataDecisionsCompetitorBullets={DATA_DECISIONS_HUBSTAFF_BULLETS}
      dataDecisionsCompetitorCardTitle="Hubstaff"
      dataDecisions2Heading="Use Case 2: Turning Activity Data Into Better Decisions"
      dataDecisions2Subtitle="TeamGrid helps you understand what's not working, so you can improve it."
      dataDecisions2TeamGridBullets={DATA_DECISIONS2_TEAMGRID_BULLETS}
      dataDecisions2CompetitorBullets={DATA_DECISIONS2_HUBSTAFF_BULLETS}
      dataDecisions2CompetitorCardTitle="Hubstaff"
      whatYouGetItems={WHAT_YOU_GET_ITEMS}
      outcomesTitle="Performance Outcomes Teams See"
      outcomesImageSrc="/Section_hubstaff_bottom.webp"
      outcomesImageAlt="Performance Outcomes Teams See - Make decisions faster, Act on insights more often, Less resistance from teams, Higher trust and engagement"
      trustedBadges={[
        "Real workforce insights not just activity logs",
        "Privacy-first by design",
        "Ask TeamGrid: simple AI chat for instant team reports",
      ]}
      faqs={FAQ_ITEMS}
      faqTitle="Frequently Asked Questions"
      faqSubtitle="Common questions about TeamGrid vs. Hubstaff."
    />
  );
}
